import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "./ui/button";
import { TOURS } from "../walkthrough/tours";

export function startTour(id) {
  window.dispatchEvent(new CustomEvent("kg:start-tour", { detail: id }));
}

export default function Walkthrough() {
  const [tour, setTour] = useState(null);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const nav = useNavigate();
  const loc = useLocation();

  const finish = useCallback(() => {
    if (tour) {
      try {
        const done = JSON.parse(localStorage.getItem("kg_tours_completed") || "[]");
        if (!done.includes(tour.id)) {
          localStorage.setItem("kg_tours_completed", JSON.stringify([...done, tour.id]));
        }
        window.dispatchEvent(new CustomEvent("kg:tour-finished", { detail: tour.id }));
      } catch { /* storage unavailable */ }
    }
    setTour(null);
    setIdx(0);
    setRect(null);
  }, [tour]);

  useEffect(() => {
    const on = (e) => {
      const t = TOURS.find((x) => x.id === e.detail);
      if (!t) return;
      setRect(null);
      setIdx(0);
      setTour(t);
      if (loc.pathname !== t.route) nav(t.route);
    };
    window.addEventListener("kg:start-tour", on);
    return () => window.removeEventListener("kg:start-tour", on);
  }, [loc.pathname, nav]);

  const step = tour?.steps[idx];

  useEffect(() => {
    if (!tour || !step) return undefined;
    let tries = 0;
    let timer;
    const find = () => {
      const el = document.querySelector(`[data-testid="${step.target}"]`);
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        setRect(el.getBoundingClientRect());
        return;
      }
      tries += 1;
      if (tries > 25) {
        if (idx < tour.steps.length - 1) setIdx(idx + 1);
        else finish();
        return;
      }
      timer = setTimeout(find, 200);
    };
    timer = setTimeout(find, 300);
    return () => clearTimeout(timer);
  }, [tour, idx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!tour || !step) return undefined;
    const update = () => {
      const el = document.querySelector(`[data-testid="${step.target}"]`);
      if (el) setRect(el.getBoundingClientRect());
    };
    const iv = setInterval(update, 400);
    window.addEventListener("resize", update);
    return () => { clearInterval(iv); window.removeEventListener("resize", update); };
  }, [tour, step]);

  if (!tour || !step || !rect) return null;

  const pad = 8;
  const top = Math.max(rect.top - pad, 4);
  const left = Math.max(rect.left - pad, 4);
  const width = rect.width + pad * 2;
  const height = rect.height + pad * 2;
  const below = rect.bottom + 190 < window.innerHeight;
  const popStyle = below
    ? { top: top + height + 12, left: Math.min(left, window.innerWidth - 344) }
    : { top: Math.max(top - 190, 12), left: Math.min(left, window.innerWidth - 344) };

  return createPortal(
    <div className="fixed inset-0 z-[100] pointer-events-none" data-testid="walkthrough-overlay">
      <div
        className="absolute rounded-lg ring-2 ring-amber-400 transition-all duration-300"
        style={{ top, left, width, height, boxShadow: "0 0 0 9999px rgba(2,6,23,0.65)" }}
        data-testid="walkthrough-spotlight"
      />
      <div
        className="absolute w-[320px] rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-2xl pointer-events-auto"
        style={popStyle}
        data-testid="walkthrough-popover"
      >
        <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-600">
          {tour.title} · Step {idx + 1}/{tour.steps.length}
        </p>
        <p className="mt-1 font-display font-semibold text-sm" data-testid="walkthrough-step-title">{step.title}</p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{step.body}</p>
        <div className="mt-3 flex items-center justify-between">
          <Button size="sm" variant="ghost" data-testid="tour-skip-btn" onClick={finish} className="text-slate-400 h-8">
            <X className="mr-1 h-3.5 w-3.5" /> Skip
          </Button>
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" data-testid="tour-back-btn" disabled={idx === 0}
              onClick={() => setIdx(idx - 1)} className="h-8 w-8 p-0">
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" data-testid="tour-next-btn"
              className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900 h-8"
              onClick={() => (idx < tour.steps.length - 1 ? setIdx(idx + 1) : finish())}>
              {idx < tour.steps.length - 1 ? <>Next <ChevronRight className="ml-1 h-3.5 w-3.5" /></> : "Finish"}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
